import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import MobileBottomBar from '../../components/feature/MobileBottomBar';
import MenuFilter from './components/MenuFilter';
import MenuCategorySection from './components/MenuCategory';
import MenuItemModal from './components/MenuItemModal';
import { menuCategories, type MenuItem } from '../../mocks/menuData';

export default function MenuPage() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      // Small delay to let the page render fully before scrolling
      const timer = setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) {
          const offset = 140; // account for fixed navbar + filter bar
          const top = el.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 300);
      return () => clearTimeout(timer);
    } else {
      window.scrollTo({ top: 0 });
    }
  }, []);

  return (
    <div className="bg-cream min-h-screen">
      <Navbar />

      {/* Page Header */}
      <div className="relative pt-16 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://readdy.ai/api/search-image?query=Indian%20spices%20overhead%20colorful%20bowls%20turmeric%20saffron%20red%20chili%20coriander%20cumin%20rustic%20wooden%20background%20vibrant%20warm%20tones%20professional%20food%20photography%20artistic&width=1920&height=400&seq=1801&orientation=landscape')",
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-teal/90 via-charcoal/80 to-saffron/80"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 sm:py-28 text-center">
          <p className="font-sans text-xs uppercase tracking-[0.3em] text-saffron mb-3">Chaska Indian Kitchen</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-white mb-4">Our Menu</h1>
          <p className="font-sans text-base text-white/80 max-w-lg mx-auto">
            Authentic flavors, modern presentation — every dish is crafted with love
          </p>

          {/* Category Quick Nav */}
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            {menuCategories.map((cat) => (
              <a
                key={cat.id}
                href={`#${cat.id}`}
                className="font-sans text-xs px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white hover:bg-saffron hover:border-saffron transition-colors cursor-pointer whitespace-nowrap"
              >
                {cat.name}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <MenuFilter activeFilter={activeFilter} onChange={setActiveFilter} />

      {/* Menu Content */}
      <main className="max-w-7xl mx-auto px-6 py-10 pb-28">
        {/* Dietary Legend */}
        <div className="flex flex-wrap items-center gap-4 mb-10 p-4 bg-white rounded-2xl border border-charcoal/5">
          <p className="font-sans text-xs font-bold text-charcoal/50 uppercase tracking-wider">Legend:</p>
          <div className="flex items-center gap-1.5">
            <span className="w-4 h-4 flex items-center justify-center rounded-sm border border-green-600">
              <span className="w-2 h-2 rounded-full bg-green-600 block"></span>
            </span>
            <span className="font-sans text-xs text-charcoal/70">Vegetarian</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-4 h-4 flex items-center justify-center rounded-sm border border-red-600">
              <span className="w-2 h-2 rounded-full bg-red-600 block"></span>
            </span>
            <span className="font-sans text-xs text-charcoal/70">Non-Vegetarian</span>
          </div>
          <div className="flex items-center gap-1.5">
            <i className="ri-fire-fill text-coral text-sm"></i>
            <span className="font-sans text-xs text-charcoal/70">Spicy</span>
          </div>
          <p className="font-sans text-xs text-charcoal/50 ml-auto hidden sm:block">
            Tap any item to see full details
          </p>
        </div>

        {menuCategories.map((cat) => (
          <MenuCategorySection
            key={cat.id}
            category={cat}
            activeFilter={activeFilter}
            onItemClick={setSelectedItem}
          />
        ))}

        {/* Allergen Note */}
        <div className="mt-8 p-5 rounded-2xl bg-turmeric/10 border border-turmeric/20">
          <div className="flex items-start gap-3">
            <i className="ri-information-line text-turmeric text-xl mt-0.5"></i>
            <div>
              <p className="font-sans font-semibold text-sm text-charcoal mb-1">Allergen &amp; Dietary Information</p>
              <p className="font-sans text-xs text-charcoal/60 leading-relaxed">
                Our kitchen handles common allergens including nuts, dairy, gluten, and shellfish. Please inform your server of any allergies or dietary restrictions. Menu items and prices may vary. All curry dishes can be made with Chicken, Lamb, Goat, Shrimp, or Paneer unless otherwise specified.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <MobileBottomBar />
      <MenuItemModal item={selectedItem} onClose={() => setSelectedItem(null)} />
    </div>
  );
}
